import template from './datasets-records.hbs'
import recordTemplate from './datasets-record.hbs'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import formatValue from 'scoreboard/components/format-value'
import excelExport from 'scoreboard/components/export/excel-export'
import RecordForm from './record-form'
import appDisplay from 'scoreboard/components/app-display'
import $ from 'jquery'
import DatasetsQueryView from '../datasets-query-view'
import constants from 'scoreboard/components/constants'
import editedIndicator from './edited-indicator.hbs'
import Tooltip from 'scoreboard/components/tooltip'
import DeleteConfirmation from 'scoreboard/components/delete-confirmation'
import HeaderControlsPositioner from 'scoreboard/components/header-controls-positioner'
import deviceType from 'scoreboard/components/device-type'
import LoadingDots from 'scoreboard/components/loading-dots'
import noPermission from 'scoreboard/components/no-permission.hbs'
import InfiniteScroller from 'scoreboard/components/infinite-scroller'
import JsonStreamer from 'scoreboard/components/json-streamer'
import { streamLoaderPromise }  from 'scoreboard/components/export/stream-loader'
import scrollContainer from 'scoreboard/components/scroll-container'
import FieldValueFormatter from './field-value-formatter'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import ErrorView from 'scoreboard/components/error-view'
import isRtl from 'scoreboard/components/is-rtl'
import datasetFieldTimeFormat from 'scoreboard/datasets/dataset-field-time-format'
import DataType from 'scoreboard/datasets/model/data-type'
const { TEXT } = DataType

/**
 * The Datasets | Records screen.
 *
 * @property {Dataset} model
 * @property {Object} request the DatasetsQueryRequest that was sent to the server
 * @property {DatasetsQueryResponse} response server response without the records
 */
var DatasetsRecords = DatasetsQueryView.extend({

	className: 'display-layout table-hack datasets-records ' + DatasetsQueryView.prototype.className,

	events: _.defaults({
		'click .records .add': 'addRecord',
		// important: deleteRecord goes before editRecord so deleteRecord can .stopPropagation()
		'click .records .is-editable .delete': 'deleteRecord',
		'click .records tbody tr.is-editable': 'editRecord',
		'mouseenter .records-table tbody tr': 'rowButtonsPosition',
		'mouseenter .edited .indicator': 'enterEditedIndicator',
		'mouseleave .edited .indicator': 'leaveEditedIndicator',
		'click .reset-filters': 'resetFilters'
	}, DatasetsQueryView.prototype.events),

	// retrieve records when new queries are retrieved
	records: true,
	// The API route to invoke when export unsaved changes
	unsavedExportRoute: 'datasets/records',
	// see app-display.js, used for loading tables before export
	deferredReady: true,
	// retrieve the total when new queries are retrieved
	total: true,

	maxRecords: () => deviceType.touchOnly ? 100 : constants.DATASETS_MAX_RECORDS,

	/**
	 * (See DatasetsQueryView)
	 */
	initialize(options = {}) {
		DatasetsQueryView.prototype.initialize.apply(this, arguments)
		this.recordContexts = []
		// #52191 when on the records tab, only display permissions that include
		// the "View Records" option
		this.filtersView.onlyViewRecords = true
		// #52177 quick hack to make sure we're showing permission filters based
		// on the stricter standard of viewing individual records
		this.filtersView.hasUnfilteredPermission = () =>
			this.model.get('hasUnfilteredRecordsPermission')
	},

	cancel() {
		this._records = this.originalResponse.records
		DatasetsQueryView.prototype.cancel.apply(this, arguments)
	},

	// Streaming Setup
	/**
	 * Overrides datasets-query-view in order to setup a new streamer
	 * Used to fetch records when the filter is changed
	 */
	executeQuery(request) {
		this.options.initialData = []
		return new Promise((resolve, reject) => {
			var streamer = new JsonStreamer()
			this._streamer = streamer
			const response = {}
			this.listenTo(streamer, 'data', (propertyName, value) => {
				response[propertyName] = value
			})

			this.listenToOnce(streamer, 'stream', stream => {
				this._records = []
				stream.once('data', d => {
					this.options.initialData = d
					this.setupStream(stream)
					resolve(response)
				})
				stream.once('end', () => resolve(response))
			})

			streamer.start({
				stream: 'records',
				method: 'POST',
				url: 'api/datasets/' + this.model.id + '/query',
				data: JSON.stringify(request),
				error: error => reject(error)
			})
		})
	},

	setupStream(stream) {
		_.result(this._stream, 'removeAllListeners')
		this._records = this.options.initialData
		this.originalResponse.records = _.cloneDeep(this.options.initialData)
		this._stream = stream
		this.fullyLoaded = this._stream.endEmitted || false // Used for export view
		this._stream.on('data', d => this.renderRecordData(d))
		this._stream.on('end', () => this.fullyLoaded = true)
		_.invoke(this.loadingDots, 'observeStream', this._stream)
	},

	setupInfiniteScroller: function () {
		if (this._stream) {
			this._scroller = new InfiniteScroller({el: scrollContainer(this.el) })
			// Must be registered before calls to checkScroll() or data will not be rendered
			this._stream.on('data', this._scroller.dataReady.bind(this._scroller))
			this._scroller.on('scroll', this._stream.next.bind(this._stream))
			this._scroller.checkScroll()
		}
	},

	// Rendering
	render: function() {
		this.createHeader('records')

		this.$el.empty().append(this.header.el)
		this.header.render()

		this.renderQueryResults()
		this.togglePeriodDisplay()
		this.renderSaveControls()

		this.secure()
		this.guard()

		this.showRecalculationBannerIfNeeded()
		return this
	},

	renderRecordData: function(newRecords) {
		if (newRecords) {
			const rows = _.reduce(newRecords, (acc,r) => {
				const recordCount = this._records.push(r)
				// Must add for cancel to work correctly
				this.originalResponse.records.push(r)
				const ctx = this.recordContext(r, recordCount-1 /* recordIndex */)
				return acc + recordTemplate(ctx)
			}, '')
			this.$('.records-table tbody').append(rows)
		}
	},

	loadingQueryResults: function() {
		this.loadingDots.render()
		// Remove the current total while it calculates
		this.$('.records h3').text(i18n.get('datasets.records'))
		// Remove the content from the records panel
		this.$('.records.panel .panel-content > :not(.loading-dots-container)')
			.remove()
	},

	renderQueryResults: function() {
		_.invokeMap([this.covertButtonTooltips], 'cleanup')

		// render the display pane.  this does the bulk of the work
		var html = template(this.context())
		if (_.isEmpty(this.$('.display-pane-body'))) {
			this.$el.append(html)
		} else {
			this.$('.display-pane-body').replaceWith(html)
		}

		this.loadingDots = new LoadingDots({
			el: this.$('.loading-dots-container')
		})

		this.prepFilters()

		this.renderActiveFilters()
		// setup covert buttons
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		// toggle add/edit controls based on whether new records are allowed in
		// the dataset, and whether there are some editable fields

		// only show the add button if new records are allowed
		this.$('.records .panel-header .add').toggleClass('is-not-displayed', !this.model.get('newAllowed'))

		// Renders any records that were streamed before we can handle 'data' events in renderRecordData
		this.$('.records-table tbody').append(_.reduce(this._records, (acc, record, i) => {
			return acc + recordTemplate(this.recordContext(record, i))
		}, ''))

		// This must not be invoked until the initialData has been received (#17150)
		this.setupInfiniteScroller()

		// Wait until everything else has been rendered. Otherwise, the add-records
		// button can be in the wrong position on load.
		this._setScrollListeners()

		return this
	},

	context: function() {
		// re-build anytime records are (re)created from a new query
		this.recordContexts = []

		// sort fields by field label
		var sortedFields = _.sortBy(this.model.get('fields'), function(field) {
			return field.label.toLowerCase()
		})
		// #16373 don't show fields to non-admins that are supposed to be hidden from them
		sortedFields = _.filter(sortedFields, field =>
			(this.model.isModifyDataset()
			|| !field.hideFromNonAdmins))

		// an array of indexes which correspond to the indexes within
		// this.model.get('fields') when the fields are properly sorted.  Doing
		// this here so we don't have to do the sort over and over for every row
		this.sortedIndexes = _.map(sortedFields, function(sortedField) {
			return _.findIndex(this.model.get('fields'), function(field) {
				return field.id == sortedField.id
			}.bind(this))
		}.bind(this))

		this.fieldValueFormatter = new FieldValueFormatter(this.model, this.treeItemsById)

		// don't display the total if there was an error
		const totalError = _.get(this.options, 'response.totalError')
		const metadata = this.model.get('dataSourceMetadata')
		// don't display the total if there's no data source
		const hasDataSource = metadata && !_.isNil(metadata.source)
		let countMessage
		if (_.isNil(totalError) && hasDataSource) {
			countMessage =  DatasetsRecords.countMessage(this.response.numResults,
				this.maxRecords())
		}

		return {
			hasRecords: this.response.numResults > 0,
			countMessage,
			noRecords: i18n.get('common.thereAreNo', 'datasets.records'),
			fields: sortedFields
		}
	},

	recordContext: function(record, recordIndex) {
		var edited = false
		// this.response.edited will be nil when the dataset doesn't have a
		// primary key
		if (!_.isNil(this.response.edited)) {
			_.each(this.response.edited[recordIndex], function(e) {
				if (e) {
					edited = true
					return false
				}
			})
		}

		const fieldAttributes = _.compact(_.map(this.sortedIndexes, index => {
			let field = this.model.get('fields')[index]
			field = this.model.getUnderlyingField(field)
			// #16373 hide fields that should only be shown to admins, if appropriate
			if (!this.model.isModifyDataset() && field.hideFromNonAdmins) {
				return null
			}
			var value = record[index]
			let displayValue = this.fieldValueFormatter.format(field, value)

			return {
				value,
				displayValue: displayValue?.toString(),
				dataType: field.dataType,
				timeFormat: datasetFieldTimeFormat(field)
			}
		}))

		let newValue = _.get(this.response, 'new.' + recordIndex, false)
		let editable = _.get(this.response, 'editable.' + recordIndex)

		const ctx = {
			// this.response.new won't be populated if the dataset doesn't have
			// a primary key
			'new': newValue,
			edited: edited || !!newValue,
			editable: this.model.isModifyRecords() && (newValue || editable),
			values: fieldAttributes
		}
		this.recordContexts.push(ctx)
		return ctx
	},

	secure: function() {
		DatasetsQueryView.prototype.secure.apply(this, arguments)
		if (!this.model.isModifyRecords()) {
			this.$('.records .add').remove()
		}

		let permissionError = DatasetsRecords.testRecordsPermissions(
			!this.model.isViewRecords(),
			!this.model.isViewRecordsTab()
		)
		// User is missing Record related permissions if permissionError is present
		if (permissionError) {
			let message = i18n.get(permissionError)
			let context = { message, hideContactAdminMessage: true }
			this.$('.scroll-container').html(noPermission(context))
		}
	},

	addRecord: function() {
		var xhr = $.get('api/datasets/' + this.model.id + '/field-suggestions')
		appDisplay.dialog(xhr, function(suggestions) {
			var recordForm = new RecordForm({
				model: this.model,
				treeItemsById: this.treeItemsById,
				record: {
					suggestions: suggestions
				}
			})
			// FIXME: for now just refresh the display, but later on should
			// do that more gracefully
			recordForm.on('done', function(data) {
				// don't pass along the data to the query method
				this.query()
			}.bind(this))
			return recordForm
		}.bind(this))
	},

	editRecord: function(e) {
		const record = this._records[this.rowIndex(e)]
		// generated_key is always included last with raw records streamed from the backend
		const params = { generatedKey: _.last(record) }
		const url = `api/datasets/${this.model.id}/records?${$.param(params)}`
		const xhr = $.get({
			url,
			error: (err) => {
				const errorView = new ErrorView(err.responseJSON)
				appDisplay.dialog(errorView)
			}
		})
		appDisplay.dialog(xhr, (record) => {
			const responseTreeItemsById = _.keyBy(record.treeItems, 'id')
			// #46706 no reason not to add the tree items that were relevant to the individual
			// record to this view's cache of tree items
			_.extend(this.treeItemsById, responseTreeItemsById)
			const recordForm = new RecordForm({
				model: this.model,
				treeItemsById: this.treeItemsById,
				record
			})
			// FIXME: for now just refresh the display, but later on should
			// do that more gracefully
			recordForm.on('done', () => {
				// don't pass along the data to the query method
				this.query()
			})
			return recordForm
		})
	},

	deleteRecord: function(e) {
		e.stopPropagation()
		const index = this.rowIndex(e)
		const record = this._records[index]
		const generatedKey = _.last(record)
		const recordContext = this.recordContexts[index]
		const recordAttributes = recordContext.values.find(value => value.value == generatedKey)
		let label = recordAttributes && recordAttributes.displayValue
		if (!_.isNil(label) && _.get(recordAttributes, 'dataType') === TEXT) {
			// #46788 the displayValue for text datatypes has been escaped once
			// and delete-confirmation will also escape it, so unescape it here
			label = _.unescape(label)
		}
		let message
		if (_.isNil(label)) {
			message = i18n.get('common.delete.confirmation.message', i18n.get('datasets.thisRecord'))
		}
		const confirmation = new DeleteConfirmation({
			label,
			message
		})
		appDisplay.dialog(confirmation)
		confirmation.once('confirm', () => {
			this._confirmDeleteRecord(e, index, generatedKey)
			confirmation.trigger('done')
		})
	},

	_confirmDeleteRecord: function(e, index, generatedKey) {
		const params = { generatedKey }
		$.delete('api/datasets/' + this.model.id + '/records?' + $.param(params), () => {
			this._records.splice(index, 1)
			this.recordContexts.splice(index, 1)
			this.response.edited.splice(index, 1)
			this.response.new.splice(index, 1)
			this.response.numResults--
			this.render()
		})
	},

	exportModelLookup(tableId, tableRowIndex, jqCell) {
		let cellData = {}
		const colIndex = jqCell.index()
		if (tableRowIndex > 0 && colIndex > 0) { // ignore header and row controls
			const record = this.recordContexts[tableRowIndex-1]
			const field = record.values[colIndex-1] // -1 for row controls cell
			let {value, displayValue, dataType, timeFormat} = field
			if (isGeographicOrRollup(dataType)) {
				value = displayValue
			}
			cellData = { value, dataType, timeFormat }
		}
		return cellData
	},

	exportTable() {
		return streamLoaderPromise(this, this._stream).then(() => {
			var opts = {
				label: this.model.get('label'),
				dataAttributes: {
					modelLookup: this.exportModelLookup.bind(this)
				},
				// The first column is empty (used to render row controls in the UI)
				// we must omit it
				interceptRows: rows => _.each(rows, r => r.shift())
			}
			return excelExport.saveTableAsFile(this.$('.records-table tbody'), opts)
		})
	},

	// figures out the index of the row the user interacted with
	rowIndex: function(e) {
		return this.$(e.target).closest('tr').index()
	},

	// When the view is resized, reset calculations which govern the position
	// of the row edit button(s) so they'll appear correctly upon row hover
	adjustRowButtons: function() {
		// Because the records table has the potential to overflow out of the viewable area,
		// we need to make sure the buttons remain in view. The filters table
		// width never overflows, so we can grab its width to base the control's positioning.
		var activeWidth = this.$('.filters').width()
		var recordsWidth = this.$('.records-table').width()

		// If table overflows, and user scrolls to see more of its data,
		// we need to adjust the position based on how far it's been scrolled
		const scrollContainer = $('.scroll-container')[0]
		var scrollPosition = isRtl() ? -scrollContainer.scrollLeft : scrollContainer.scrollLeft

		// If tables aren't equal, need to compensate for the
		// difference so there isn't a gap on the right.
		// 25 = max distance from right of records table to right edge of window
		var tableWidthDifference = recordsWidth - activeWidth >= 25 ? 25 : recordsWidth - activeWidth
		let buttonsWidth = 150
		var leftAdjust = buttonsWidth + tableWidthDifference - scrollPosition

		// Calculate rowButtonCompensation, considering RTL
		let rowButtonCompensation = _.min([activeWidth - leftAdjust, recordsWidth + 42 - 207])
		this.rowButtonCompensation = isRtl() ? -rowButtonCompensation : rowButtonCompensation

		// If we're hovered already over a row and the buttons are showing, adjust their position and height
		if (!deviceType.isMobile() && this.currentRow) {
			// When we resize, we need to cancel out any previous height defined on .buttons
			// or else, it might end up propping up the row taller than it should be
			this.$('.buttons').height('auto')

			this.currentRow
				.find('.buttons')
				.css('height', this.currentRow.height() - 1) // minus 1px to prevent odd height shift when scrolled at bottom of page. Issue #18544
				.css('transform', 'translateX(' + this.rowButtonCompensation + 'px)')
		} else {
			this.mobileButtonsPosition()
		}

		// Also, set the header control position here as well
		this.controlsPositioner.setHeaderControlsPosition()
	},

	// Because each row has edit/buttons, and the table can potentially overflow, we can't just
	// add buttons to the last `td`, or else they could be hidden. Instead we add them
	// to the first td, and use translateX to move it horizontally across the table.
	rowButtonsPosition: function(e) {
		var target = e.currentTarget || e // If mobile, 'e' will already be an element
		this.currentRow = $(target)
		// Push all .buttons's in the table rows all the way to right, but remain in viewable area
		var el = target.getElementsByClassName('buttons')[0]
		if (el) {
			el.style.transform = 'translateX(' + this.rowButtonCompensation + 'px)'
			el.style.height = target.clientHeight  - 1 + 'px'
		}
	},

	// Record table edit buttons are always shown on mobile devices, so we need to set their
	// positions on load. On desktop we will lazily position them on individual row hovers.
	mobileButtonsPosition: function() {
		if (deviceType.isMobile()) {
			_.forEach(this.$('.records-table tbody tr'), el => this.rowButtonsPosition(el))
		}
	},

	enterEditedIndicator: function(e) {
		var index = this.rowIndex(e)
		var editedRow = this.response.edited[index]
		var fieldLabels = []
		for (var i=0; i<editedRow.length; i++) {
			if (editedRow[i]) {
				fieldLabels.push(this.model.get('fields')[i].label)
			}
		}
		fieldLabels = _.sortBy(fieldLabels, function(fieldLabel) {
			return fieldLabel.toLowerCase()
		})
		var fields = _.join(fieldLabels, ', ')
		var content = editedIndicator({
			fields: fields,
			new: this.response.new[index]
		})
		this.tooltip = new Tooltip({
			attachTo: $(e.currentTarget),
			content: content,
			placement: 'above',
			arrow: 'center'
		}).render()
	},

	leaveEditedIndicator: function(e) {
		_.result(this.tooltip, 'remove')
	},

	resetFilters: function() {
		this.dirty(true)
		this._setFilters([])
		this.query()
	},

	_setScrollListeners: function () {
		this.mobileButtonsPosition()
		this.on('resize', () => {
			this.mobileButtonsPosition()
			this.adjustRowButtons()
		})

		this.controlsPositioner = new HeaderControlsPositioner({
			positionedElement: this.$('.records .right-buttons'),
			scrollElement: this.$('.records'),
			scrollContainer: this.$('.scroll-container')
		})
		this.controlsPositioner.setHeaderControlsPosition()
		this.adjustRowButtons()

		this.$('.scroll-container').scroll(function() {
			this.controlsPositioner.setHeaderControlsPosition()
			this.adjustRowButtons()
		}.bind(this))
	},

	remove: function() {
		_.invoke(this._streamer, 'abort') // reference to streamer last used to modify filters
		DatasetsQueryView.prototype.remove.apply(this, arguments)
		_.invokeMap(this.covertButtonTooltips, 'remove')
		_.invokeMap([this.tooltip, this.loadingDots, this._scroller], 'remove')
	}

}, {

	/**
	 * Constructs a message indicating how many records there are for the
	 * Records panel.  For example (432) or (1,000 or 1,234)
	 */
	countMessage: function(numResults, maxResults) {
		var formattedNumResults = formatValue({ value: numResults, shorten: false })
		if (numResults <= maxResults) {
			return '(' + formattedNumResults + ')'
		} else {
			return i18n.get('common.index',
				formatValue({ value: maxResults, shorten: false }),
				formattedNumResults)
		}
	},

	/**
	 * Tests to see if a user can view the Records tab.
	 * Returns a message resource if the appropriate
	 * permissions are not present.
	 * @param cannotViewRecords Records viewing permission
	 * @param cannotViewRecordsTab Records tab viewing permission
	 * @returns {string|null} message resource to be returned
	 */
	testRecordsPermissions: function(cannotViewRecords, cannotViewRecordsTab) {
		if (cannotViewRecords || cannotViewRecordsTab) {
			return cannotViewRecords ?
				'datasets.permissions.noViewRecords' :
				'datasets.permissions.noViewRecordsTab'
		} else {
			return null
		}
	}
})

export default DatasetsRecords

DatasetsRecords.__filename__ = 'datasets/records/datasets-records.js'
